import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { api, fetchWithAuth } from "@/lib/api";
import { portalApi, PortalInvite } from "@/lib/portalApi";
import { Send, Calendar, Loader2, Clock } from "lucide-react";

export function SendNotificationModal({
  projectId, projectTitle, accountId, onClose, onSent,
}: {
  projectId: string;
  projectTitle: string;
  accountId: string;
  onClose: () => void;
  onSent?: (ts: string) => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<"now" | "schedule">("now");
  const [scheduledFor, setScheduledFor] = useState("");

  const { data: invites = [], isLoading } = useQuery<PortalInvite[]>({
    queryKey: ["portal-invites", accountId],
    queryFn: () => portalApi.listInvites(accountId),
  });

  const { data: pending = [] } = useQuery<{ id: string; scheduled_for: string; contacts: string[] }[]>({
    queryKey: ["scheduled-reminders", projectId],
    queryFn: () => api.getScheduledReminders(projectId),
  });

  const activeInvites = invites.filter(i => !i.revoked_at);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(activeInvites.map(i => i.email)));

  const prevCount = useRef(0);
  if (activeInvites.length !== prevCount.current) {
    prevCount.current = activeInvites.length;
    setSelected(new Set(activeInvites.map(i => i.email)));
  }

  const sendNowMutation = useMutation({
    mutationFn: () =>
      fetchWithAuth<{ ok: boolean; sent_to: string[]; task_count: number }>(
        `/api/projects/${projectId}/notify-client-tasks`,
        { method: "POST", body: JSON.stringify({ contacts: Array.from(selected) }) }
      ),
    onSuccess: (data) => {
      const ts = new Date().toISOString();
      onSent?.(ts);
      toast({ title: "Notification sent", description: `Emailed ${data.sent_to.length} contact(s) about ${data.task_count} task(s).` });
      onClose();
    },
    onError: (err: any) => toast({ title: "Failed to send", description: err.message, variant: "destructive" }),
  });

  const scheduleMutation = useMutation({
    mutationFn: () => api.scheduleClientReminder(projectId, Array.from(selected), scheduledFor),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-reminders", projectId] });
      toast({ title: "Reminder scheduled", description: `Will send to ${selected.size} contact(s) at ${new Date(scheduledFor).toLocaleString()}.` });
      onClose();
    },
    onError: (err: any) => toast({ title: "Failed to schedule", description: err.message, variant: "destructive" }),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.cancelScheduledReminder(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["scheduled-reminders", projectId] }),
  });

  const toggle = (email: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  };

  const minDateTime = new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            Client Task Notification
          </DialogTitle>
          <DialogDescription>
            {projectTitle} — Email outstanding client tasks to selected contacts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex rounded-md border overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => setMode("now")}
              className={`flex-1 py-1.5 flex items-center justify-center gap-1.5 transition-colors ${mode === "now" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
            >
              <Send className="w-3.5 h-3.5" /> Send Now
            </button>
            <button
              type="button"
              onClick={() => setMode("schedule")}
              className={`flex-1 py-1.5 flex items-center justify-center gap-1.5 transition-colors ${mode === "schedule" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
            >
              <Calendar className="w-3.5 h-3.5" /> Schedule
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading contacts…
            </div>
          ) : activeInvites.length === 0 ? (
            <p className="text-sm text-muted-foreground">No active portal invites for this client. Generate an invite first.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Send To</p>
              {activeInvites.map((invite) => (
                <div key={invite.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`contact-${invite.id}`}
                    checked={selected.has(invite.email)}
                    onCheckedChange={() => toggle(invite.email)}
                  />
                  <label htmlFor={`contact-${invite.id}`} className="text-sm cursor-pointer flex items-center gap-2">
                    {invite.email}
                    {invite.accepted_at
                      ? <span className="text-[10px] text-green-600 font-medium">Accepted</span>
                      : <span className="text-[10px] text-yellow-600 font-medium">Pending</span>}
                  </label>
                </div>
              ))}
            </div>
          )}

          {mode === "schedule" && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Send at</p>
              <input
                type="datetime-local"
                min={minDateTime}
                value={scheduledFor}
                onChange={e => setScheduledFor(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}

          {pending.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pending reminders</p>
              {pending.map(r => (
                <div key={r.id} className="flex items-center justify-between text-sm bg-muted/40 rounded px-2 py-1.5">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                    {new Date(r.scheduled_for).toLocaleString()}
                    <span className="text-xs text-muted-foreground">({r.contacts.length} contact{r.contacts.length !== 1 ? "s" : ""})</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => cancelMutation.mutate(r.id)}
                    className="text-xs text-destructive hover:underline"
                  >
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {mode === "now" ? (
            <Button
              onClick={() => sendNowMutation.mutate()}
              disabled={selected.size === 0 || sendNowMutation.isPending || activeInvites.length === 0}
            >
              {sendNowMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Send Now
            </Button>
          ) : (
            <Button
              onClick={() => scheduleMutation.mutate()}
              disabled={selected.size === 0 || !scheduledFor || scheduleMutation.isPending || activeInvites.length === 0}
            >
              {scheduleMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Calendar className="w-3.5 h-3.5 mr-1.5" />}
              Schedule
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
